import React, { useEffect, useCallback, useMemo } from 'react';
import { Support } from '../../hooks/Support';
import Hls from 'hls.js';

export type AirPlayProps = {
  authorization: string;
  enabled: boolean;
  hls: Hls;
  code: string;
  video: string;
  videoName: string;
  videoPoster: string;
  supported: Support;
  airplayAvailable: {
    isAirplayAvailable: boolean;
    setIsAirplayAvailable: React.Dispatch<React.SetStateAction<boolean>>
  };
  airplayConnected: {
    isAirplayConnected: boolean;
    setIsAirplayConnected: React.Dispatch<React.SetStateAction<boolean>>
  };
};

declare global {
  interface HTMLElement {
    webkitShowPlaybackTargetPicker: () => Promise<void>;
  }

  interface HTMLVideoElement {
    webkitCurrentPlaybackTargetIsWireless: boolean;
  }
}

const AirPlay = ({ authorization, enabled, hls, code, video, videoName, videoPoster, supported, airplayAvailable, airplayConnected }: AirPlayProps) => {
  const { isAirplayAvailable, setIsAirplayAvailable } = airplayAvailable;
  const { isAirplayConnected, setIsAirplayConnected } = airplayConnected;

  const airPlayVideo = useCallback(() => {
    if (!supported.airplay || !hls.media) {
      return;
    }

    // const source = document.createElement('source');
    // source.src = video;

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const videoElement = rootElement.querySelector('.smartplayer__videoHls') as HTMLVideoElement;
    videoElement.disableRemotePlayback = true;
    // videoElement.appendChild(source);

    const video = rootElement.querySelector('.smartplayer__airplay') as HTMLVideoElement;
    video.disableRemotePlayback = false;

    console.log('isAirplayConnected', isAirplayConnected);

    if (isAirplayConnected || video?.webkitCurrentPlaybackTargetIsWireless) {

      if (!video.paused) {
        video.pause();
      }

      if (!!(video as any).webkitExitFullscreen) {
        (video as any).webkitExitFullscreen();
      }

      if ((video as any).webkitWirelessVideoPlaybackDisabled !== undefined) {
        (video as any).webkitWirelessVideoPlaybackDisabled = true;
      }

      if ((video as any).disableRemotePlayback !== undefined) {
        video.disableRemotePlayback = true;
      }

      if (window.AudioContext) {
        const audioContext = new AudioContext();
        audioContext.close();
      }

      setIsAirplayConnected(false);

      video.disableRemotePlayback = false;
    } else if (video?.webkitCurrentPlaybackTargetIsAirPlay) {
      video?.play();
    } else if (video?.webkitCurrentPlaybackTargetIsWireless) {
      video?.play();
    } else {
      video?.webkitShowPlaybackTargetPicker();
      setIsAirplayConnected(true);
    }
    // hls.media.webkitShowPlaybackTargetPicker();
  }, [hls, code, video, supported.airplay, isAirplayConnected]);

  const onAirplayOpen = useCallback(() => {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    rootElement.classList.add('smartplayer__airplay--active');

    const video = rootElement.querySelector('.smartplayer__airplay') as HTMLVideoElement;
    video?.play();

    setIsAirplayConnected(true);
  }, [code]);

  const onAirplayClose = useCallback(() => {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    rootElement.classList.remove('smartplayer__airplay--active');

    const video = rootElement.querySelector('.smartplayer__airplay') as HTMLVideoElement;

    if (video) {
      video.pause();
    }

    setIsAirplayConnected(false);
  }, [code]);

  useEffect(() => {
    if (!hls || !hls.media || !code) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const videoNative = rootElement.querySelector('.smartplayer__airplay') as HTMLVideoElement;

    if (videoNative) {
      videoNative.addEventListener('webkitplayingremotely', onAirplayOpen);
      videoNative.addEventListener('webkitsessionclosed', onAirplayClose);

      videoNative.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', (event) => {
        console.log('webkitcurrentplaybacktargetiswirelesschanged', event);
        setIsAirplayConnected(!!(event as any)?.target.webkitCurrentPlaybackTargetIsWireless);
      });

      videoNative.addEventListener('webkitplaybacktargetavailabilitychanged', (event) => {
        console.log('webkitplaybacktargetavailabilitychanged', event);
        setIsAirplayAvailable((event as any)?.availability === 'available');

        if ((event as any)?.availability !== 'available' && isAirplayConnected) {
          setIsAirplayConnected(false);
        }
      });

      if (videoNative?.webkitCurrentPlaybackTargetIsWireless) {
        setIsAirplayConnected(true);
      }
    }

    hls.media.addEventListener('webkitplayingremotely', onAirplayOpen);

    hls.media.addEventListener('webkitsessionclosed', onAirplayClose);

    hls.media.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', (event) => {
      event.preventDefault();
      const video = event.target as HTMLVideoElement;
      video.pause();
      setIsAirplayConnected(true);

      console.log('webkitcurrentplaybacktargetiswirelesschanged native video', event);

      setTimeout(() => {
        if (videoNative) {
          videoNative.play();
        }
      }, 100);
    });

    hls.media.addEventListener('webkitplaybacktargetavailabilitychanged', (event) => {
      console.log('webkitplaybacktargetavailabilitychanged native video', event);
    });

    return () => {
      if (!hls || !hls.media || !code) {
        return;
      }

      const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
      const videoNative = rootElement.querySelector('.smartplayer__airplay') as HTMLVideoElement;

      if (videoNative) {
        videoNative.removeEventListener('webkitplayingremotely', onAirplayOpen);
        videoNative.removeEventListener('webkitsessionclosed', onAirplayClose);
      }

      hls.media.removeEventListener('webkitplayingremotely', onAirplayOpen);
      hls.media.removeEventListener('webkitsessionclosed', onAirplayClose);
    };
  }, [hls, code]);

  return useMemo(() => (
    enabled && supported.airplay ? (
      <div className={`${isAirplayConnected ? 'smartplayer__airplay--active' : 'smartplayer__airplay--inactive'}`} onClick={() => airPlayVideo()}>
        <button className="smartplayer__bar-button" type="button" disabled={!supported.airplay}>
          <div className="icon icon--airplay-active">
            <svg xmlns="http://www.w3.org/2000/svg" height="32" width="36" viewBox="0 0 576 512" fill="currentColor">
              <path d="M480 96H96c-17.7 0-32 14.3-32 32V320c0 17.7 14.3 32 32 32h69.5l-50.7 50.7c-4 4-7.4 8.5-10.2 13.3H96c-53 0-96-43-96-96V128C0 75 43 32 96 32H480c53 0 96 43 96 96V320c0 53-43 96-96 96h-8.6c-2.8-4.8-6.2-9.2-10.2-13.3L410.5 352H480c17.7 0 32-14.3 32-32V128c0-17.7-14.3-32-32-32zM265.4 297.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8H160c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128z"/>
            </svg>
          </div>
          <div className="icon icon--airplay-inactive">
            <svg xmlns="http://www.w3.org/2000/svg" height="32" width="36" viewBox="0 0 576 512" fill="currentColor">
              <path d="M480 96H96c-17.7 0-32 14.3-32 32V320c0 17.7 14.3 32 32 32h69.5l-50.7 50.7c-4 4-7.4 8.5-10.2 13.3H96c-53 0-96-43-96-96V128C0 75 43 32 96 32H480c53 0 96 43 96 96V320c0 53-43 96-96 96h-8.6c-2.8-4.8-6.2-9.2-10.2-13.3L410.5 352H480c17.7 0 32-14.3 32-32V128c0-17.7-14.3-32-32-32zM265.4 297.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8H160c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128z"/>
            </svg>
          </div>
        </button>
      </div>
    ) : null
  ), [enabled, isAirplayConnected, supported.airplay, airPlayVideo]);
};

export default AirPlay;
